/**
 * Date helpers that are explicit about timezone.
 *
 * Streaks, daily goals, and the trip countdown are all *local-day* concepts.
 * Computing them in UTC would tell her she broke a streak at 8pm, so every
 * function here takes an IANA timezone and works on 'YYYY-MM-DD' strings.
 */

/** The whole point of the app. */
export const TRIP_DATE = '2026-11-04'

export type LocalDate = string // 'YYYY-MM-DD'

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = dateFormatterCache.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dateFormatterCache.set(timeZone, f)
  }
  return f
}

/** The calendar date it is *right now* for this user. */
export function localDate(timeZone: string, at: number = Date.now()): LocalDate {
  // 'en-CA' formats as YYYY-MM-DD, which is exactly our storage format.
  return formatter(timeZone).format(new Date(at))
}

/**
 * How far the zone sits from UTC at a given instant, in ms.
 *
 * Derived by formatting the instant in the zone and reading the wall clock
 * back, which is the only approach that stays correct across DST changes and
 * zones with non-hour offsets.
 */
function zoneOffsetMs(timeZone: string, at: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at))

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0')
  // Intl can render midnight as hour 24; Date.UTC needs 0.
  const wallClock = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  )
  return wallClock - at
}

/**
 * Epoch ms of midnight at the start of the user's current local day.
 *
 * Used for anything rationed "per day" — new flashcards, daily goals — so the
 * reset happens at her midnight rather than UTC's.
 */
export function startOfLocalDay(timeZone: string, at: number = Date.now()): number {
  const midnightAsUtc = Date.parse(`${localDate(timeZone, at)}T00:00:00Z`)
  const guess = midnightAsUtc - zoneOffsetMs(timeZone, midnightAsUtc)
  // Re-measure at the guessed instant: on a DST boundary the offset that
  // applies at local midnight can differ from the one at the same UTC time.
  const corrected = midnightAsUtc - zoneOffsetMs(timeZone, guess)
  return corrected
}

/** Whole days between two calendar dates, ignoring clock time entirely. */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  return Math.round((b - a) / 86_400_000)
}

/** Days remaining until a target date, floored at 0. */
export function daysUntil(
  target: LocalDate,
  timeZone: string,
  at: number = Date.now(),
): number {
  return Math.max(0, daysBetween(localDate(timeZone, at), target))
}

export function addDays(date: LocalDate, days: number): LocalDate {
  const ms = Date.parse(`${date}T00:00:00Z`) + days * 86_400_000
  return new Date(ms).toISOString().slice(0, 10)
}

/** Yesterday relative to a local date — the workhorse of streak walk-back. */
export function previousDate(date: LocalDate): LocalDate {
  return addDays(date, -1)
}

export function isValidLocalDate(value: string): value is LocalDate {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const ms = Date.parse(`${value}T00:00:00Z`)
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === value
}
